import React from 'react';
import { Button } from '../components/ui/button';
import { ArrowRight, Users, Award, Star, CheckCircle2, Target, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
const AboutPage = () => {
  const stats = [
    { value: '10+', label: 'Years Experience' },
    { value: '5000+', label: 'Happy Families' },
    { value: '146', label: 'Quality Checks' },
    { value: '4.9/5', label: 'Customer Rating' },
  ];

  const values = [
    {
      icon: Target,
      title: 'Our Mission',
      description: 'To transform living spaces into dream homes that reflect our clients\' unique personalities while ensuring quality, functionality, and timely delivery.'
    },
    {
      icon: Star,
      title: 'Our Vision',
      description: 'To be the most trusted interior design partner in India, known for innovation, quality, and customer satisfaction.'
    },
    {
      icon: Heart,
      title: 'Our Values',
      description: 'Integrity, creativity, quality, and customer-centricity guide everything we do. We believe in building lasting relationships.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-cream-50 via-white to-brand-light/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-brand-primary font-semibold text-sm uppercase tracking-wider">
                About Us
              </span>
              <h1 className="mt-4 text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                We Make Spaces Look <span className="text-brand-primary">Magnificent</span>, Beautiful & Functional
              </h1>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                My Insta Spaces is a trusted interior design firm in Bangalore. We create stunning living environments while guaranteeing customer satisfaction with high-quality materials and organized processes.
              </p>
              <p className="mt-4 text-gray-600 leading-relaxed">
                Having built both business and residential interiors for more than ten years, we are firm believers in the power of design, resulting in indoor settings that are not only aesthetically pleasing but also practical.
              </p>
              <div className="mt-8 flex gap-4">
                <Link to="/contact">
                  <Button className="bg-brand-primary hover:bg-brand-dark text-white px-6">
                    Get Free Consultation
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=600"
                  alt="Interior Design"
                  className="rounded-2xl shadow-lg h-64 object-cover"
                />
                <img
                  src="https://images.pexels.com/photos/2724749/pexels-photo-2724749.jpeg?auto=compress&cs=tinysrgb&w=600"
                  alt="Kitchen Design"
                  className="rounded-2xl shadow-lg h-64 object-cover mt-8"
                />
                <img
                  src="https://images.pexels.com/photos/1454806/pexels-photo-1454806.jpeg?auto=compress&cs=tinysrgb&w=600"
                  alt="Bedroom Design"
                  className="rounded-2xl shadow-lg h-64 object-cover -mt-8"
                />
                <img
                  src="https://images.pexels.com/photos/2089696/pexels-photo-2089696.jpeg?auto=compress&cs=tinysrgb&w=600"
                  alt="Modern Kitchen"
                  className="rounded-2xl shadow-lg h-64 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-brand-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white">{stat.value}</div>
                <div className="text-white/80 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              What Drives <span className="text-brand-primary">Us</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((item, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-brand-light rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-brand-primary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Why Choose <span className="text-brand-primary">My Insta Spaces?</span>
              </h2>
              <p className="mt-6 text-gray-600 leading-relaxed">
                We align core values and dynamic use of space in the creation of best interiors in Bangalore. Our commitment to excellence sets us apart.
              </p>
              <ul className="mt-8 space-y-4">
                {[
                  'Personalized designs tailored to your lifestyle',
                  'Best price guarantee with no hidden costs',
                  '45-day installation with regular updates',
                  '146 quality checks for superior finishes',
                  '10-year warranty with on-site service',
                  'Dedicated customer support team'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/1571459/pexels-photo-1571459.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Our Work"
                className="rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-brand-light rounded-full flex items-center justify-center">
                    <Award className="w-7 h-7 text-brand-primary" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">10+ Years</div>
                    <div className="text-gray-500">of Excellence</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
